import { useMemo, useRef, useState } from "react";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { useDateLocale } from "../../i18n/use-date-locale";
import { useScheduleStore } from "../../stores/schedule-store";
import { DayDial } from "./DayDial";
import { CyclicCalendar } from "./CyclicCalendar";
import { VisualizerTimezonePicker } from "./VisualizerTimezonePicker";
import { systemTimezone } from "../../lib/schedule-time/schedule-dates";
import { convertEvents, dayEvents } from "./schedule-events";
import { ReferenceSchedules } from "./ReferenceSchedules";
import { useReferenceScheduleStore } from "../../stores/reference-schedule-store";
import type { ReferenceSchedule } from "../../stores/reference-schedule-store";
import { expandScheduleSlots } from "../../lib/schedule-document/schedule-json";

const columns = "grid grid-cols-[48px_repeat(7,minmax(0,1fr))]";

export function ScheduleTimeVisualizer() {
  const { t } = useTranslation();
  const locale = useDateLocale();
  const { week, slots, timezone } = useScheduleStore();
  const [displayTimezone, setDisplayTimezone] = useState(systemTimezone);
  const references = useReferenceScheduleStore(state => state.references);
  const monday = week[0]!;
  const convertedEvents = useMemo(() => convertEvents(slots, timezone, displayTimezone), [slots, timezone, displayTimezone]);
  const referenceEvents = useMemo(() => convertReferences(references, monday, displayTimezone), [references, monday, displayTimezone]);
  const allEvents = [...convertedEvents, ...referenceEvents]; // Shared lanes make clashes sit side by side
  const scrollRef = useRef<HTMLDivElement>(null);
  const days = week.map(date => ({
    date,
    label: format(date, "EEE", { locale }),
    ownEvents: dayEvents(date, convertedEvents), 
    events: dayEvents(date, allEvents),
  }));

  return (
    <section aria-labelledby="time-visualizer-heading" className="space-y-3 border-t border-neutral-200 pt-4">
      <h2 id="time-visualizer-heading" className="text-xs font-semibold uppercase tracking-wider">{t("visualizer.title")}</h2>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <VisualizerTimezonePicker value={displayTimezone} onChange={setDisplayTimezone} date={monday} />
        <ReferenceSchedules monday={monday} />
      </div>
      <div className="custom-scrollbar overflow-x-auto rounded border border-neutral-200">
        <div className="min-w-[840px]">
          <div className={`${columns} gap-0 border-b border-neutral-200 bg-white py-2`}>
            <div />
            {days.map(day => <div key={day.date.toISOString()} className="px-1"><DayDial day={day.label} events={day.ownEvents} /></div>)}
          </div>
          <div className={`${columns} border-b border-neutral-200 bg-neutral-50`}>
            <div />{days.map(day => <div key={day.date.toISOString()} className="border-l border-neutral-200 py-2 text-center"><span className="text-[11px] font-bold">{day.label}</span><span className="ml-1 font-mono text-[10px] text-neutral-500">{format(day.date, "M/d", { locale })}</span></div>)}
          </div>
          <CyclicCalendar days={days} scrollRef={scrollRef} />
        </div>
      </div>
    </section>
  );
}

function convertReferences(references: ReferenceSchedule[], monday: Date, displayZone: string) {
  const visibleReferences = references.filter(reference => reference.visible);
  return visibleReferences.flatMap(reference => {
    const slots = expandScheduleSlots(reference.document.slots, monday);
    const tag = { id: reference.id, name: reference.name, color: reference.color };
    return convertEvents(slots, reference.document.timezone, displayZone, tag);
  });
}
